import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';

import { hasPermission } from './utils/permissions';

/**
 * The one navigation tree. The header renders the sections, the sidebar renders
 * the active section's items, and permissions gate both from here -- so the
 * header can no longer advertise a route the role cannot open.
 *
 * `anyOf` lists permissions that each grant access; an entry without one is
 * open to every signed-in user. A section shows when at least one of its items
 * survives, so an empty Admin menu never appears.
 *
 * Paths match routes.js. Keep the two in step: a path here that no route serves
 * is a dead entry, and a route with a stricter requirePermission than its entry
 * sends the user to Unauthorized.
 */
export const NAV_SECTIONS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    // No items: the dashboard is a single page, so its section opens no sidebar.
    path: '/dashboard',
    // view_issues included deliberately: the Support role holds no
    // view_projects, and without this it would be offered no Dashboard
    // section at all -- including the support preset built for it.
    anyOf: ['view_projects', 'view_issues'],
    items: [],
  },
  {
    id: 'projects',
    label: 'Projects',
    items: [
      {
        label: 'All Projects',
        path: '/projects',
        icon: FolderOutlinedIcon,
        anyOf: ['view_projects'],
        // New Project is its own entry, so match the list and the detail pages
        // but never /projects/new.
        match: (p) => p === '/projects' || /^\/projects\/\d+/.test(p),
      },
      {
        label: 'New Project',
        path: '/projects/new',
        icon: AddCircleOutlineIcon,
        anyOf: ['create_project'],
      },
    ],
  },
  {
    id: 'issues',
    label: 'Tickets',
    items: [
      {
        label: 'All Tickets',
        path: '/issues',
        icon: BugReportOutlinedIcon,
        // Mirrors issues/permissions.py CanViewIssues: managing implies viewing.
        anyOf: ['view_issues', 'create_issue', 'edit_issue'],
        match: (p) => p.startsWith('/issues'),
      },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    items: [
      {
        label: 'All Customers',
        path: '/customers',
        icon: PeopleAltOutlinedIcon,
        // A lead and a customer are the same row, so anyone who may work the
        // lead board must also reach the register. Mirrors routes.js.
        anyOf: ['view_customers', 'manage_customers', 'view_leads', 'manage_leads'],
        match: (p) => p.startsWith('/customers'),
      },
      {
        label: 'Lead Pipeline',
        path: '/leads',
        icon: ViewKanbanOutlinedIcon,
        anyOf: ['view_leads', 'manage_leads'],
      },
      {
        label: 'Sales Reports',
        path: '/sales-reports',
        icon: InsightsOutlinedIcon,
        anyOf: ['view_sales_reports'],
      },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    items: [
      {
        label: 'Payments',
        path: '/payments',
        icon: PaymentOutlinedIcon,
        anyOf: ['view_payments'],
        match: (p) => p.startsWith('/payments'),
      },
      {
        label: 'Expenses',
        path: '/expenses',
        icon: ReceiptLongOutlinedIcon,
        anyOf: ['view_expenses'],
      },
      {
        label: 'Categories',
        path: '/expense-categories',
        icon: CategoryOutlinedIcon,
        anyOf: ['view_expenses'],
      },
      {
        label: 'Expense Reports',
        path: '/expense-reports',
        icon: AssessmentOutlinedIcon,
        anyOf: ['view_expenses'],
      },
      {
        label: 'Targets',
        path: '/targets',
        icon: AccountBalanceOutlinedIcon,
        // Company money, and a separate codename from the expense ones: a
        // Financial Manager holds it, a developer never does. Mirrors
        // budgets/permissions.py and routes.js.
        //
        // Deliberately NARROWER than the /targets route, which opens on the
        // wider view_project_financials so that everyone measured against a
        // target can read their own. Putting that codename here would hand a
        // Sales Executive a whole Finance section for one entry; she reaches
        // the screen from her dashboard target card instead.
        anyOf: ['view_internal_budgets', 'manage_budgets'],
        match: (p) => p.startsWith('/targets'),
      },
    ],
  },
  {
    id: 'provisioning',
    label: 'Provisioning',
    items: [
      {
        label: 'GymViz',
        path: '/provisioning/gymviz',
        icon: FitnessCenterOutlinedIcon,
        anyOf: ['view_gymviz_provisioning', 'manage_gymviz_provisioning'],
        match: (p) => p.startsWith('/provisioning/gymviz'),
      },
      {
        label: 'AITIME',
        path: '/aitime/provisioning',
        icon: AccessTimeOutlinedIcon,
        anyOf: ['view_aitime_provisioning', 'manage_aitime_provisioning'],
        match: (p) => p.startsWith('/aitime'),
      },
      {
        label: 'Running With No Project',
        path: '/provisioning/unbilled',
        icon: AssessmentOutlinedIcon,
        // Company money, like Budgets: a Financial Manager holds this and no
        // provisioning codename, so this entry is the only reason they see a
        // Provisioning section at all. Mirrors routes.js and the
        // unbilled_provisionings widget's gate.
        anyOf: ['view_company_financials'],
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    items: [
      {
        label: 'Users',
        path: '/admin/users',
        icon: PeopleOutlinedIcon,
        anyOf: ['manage_users'],
      },
      {
        label: 'Roles',
        path: '/admin/roles',
        icon: LockOutlinedIcon,
        anyOf: ['manage_roles'],
        match: (p) => p.startsWith('/admin/roles'),
      },
      {
        label: 'Permissions',
        path: '/admin/permissions',
        icon: SettingsOutlinedIcon,
        anyOf: ['manage_roles'],
      },
      {
        label: 'API Tokens',
        path: '/admin/api-tokens',
        icon: VpnKeyOutlinedIcon,
        // System-to-system credentials. The entry was visible to every signed-in
        // user; this hides it from anyone who cannot manage users. The ROUTE is
        // still open to all -- routes.js has no requirePermission on it.
        anyOf: ['manage_users'],
      },
      {
        label: 'Application Types',
        path: '/admin/application-types',
        icon: AppsOutlinedIcon,
        anyOf: ['manage_application_types'],
      },
      {
        label: 'Implementation Methods',
        path: '/admin/implementation-methods',
        icon: ListAltOutlinedIcon,
        anyOf: ['manage_application_types'],
      },
      {
        label: 'Payment Methods',
        path: '/admin/payment-methods',
        icon: PaymentOutlinedIcon,
        anyOf: ['manage_application_types'],
      },
      {
        label: 'Distributors',
        path: '/admin/distributors',
        icon: StorefrontOutlinedIcon,
        anyOf: ['manage_distributors'],
      },
    ],
  },
];

/** Section icons, for the mobile drawer where the whole tree is listed. */
export const SECTION_ICONS = {
  dashboard: DashboardOutlinedIcon,
  projects: FolderOutlinedIcon,
  customers: PeopleAltOutlinedIcon,
  finance: PaymentOutlinedIcon,
  provisioning: RocketLaunchOutlinedIcon,
  admin: SettingsOutlinedIcon,
};

const permits = (permissions, entry) =>
  !entry.anyOf || entry.anyOf.some((p) => hasPermission(permissions, p));

/**
 * The sections this user may see, each carrying only the items they may open.
 * A section with a `path` and no items (Dashboard) is judged on its own
 * permissions; every other section is judged on its surviving items.
 */
export const visibleSections = (permissions) =>
  NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => permits(permissions, item)),
  })).filter(
    (section) => section.items.length > 0 || (section.path && permits(permissions, section)),
  );

/** True when `pathname` is inside this entry. */
export const matchesEntry = (entry, pathname) =>
  entry.match ? entry.match(pathname) : pathname === entry.path;

/**
 * The section the current URL belongs to, or null.
 *
 * Longest matching path wins, so /admin/roles resolves to Admin rather than to
 * whichever section happens to be declared first.
 */
export const activeSection = (sections, pathname) => {
  let best = null;
  let bestLength = -1;
  sections.forEach((section) => {
    const paths = section.items.length ? section.items.map((i) => i.path) : [section.path];
    paths.forEach((path) => {
      if (!path) return;
      const inside = pathname === path || pathname.startsWith(`${path}/`);
      if (inside && path.length > bestLength) {
        best = section;
        bestLength = path.length;
      }
    });
  });
  return best;
};
